import React, { useEffect, useState } from "react";
import Greeting from "../../../Widgets/greeting/Greeting";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import AccountStats from "../../../../pages/account/widgets/AccountStats";
import moment from "moment";
import { generateWeeks } from "../../../../utils/weeks";
import { getShiftsRemainingLeavesWeeklyHours } from "../../../../services/employee.service";
import { currentYear } from "../../../../features/Time Slice/timeSlice";

interface MonthObj { name: string; monthName: string; label: string; }
interface YearObj  { name: string; year: string; label: string; }

interface NurseHeaderProps {
  month?: MonthObj;
  year?: { year: string | number; [key: string]: any };
  setMonth?: (m: MonthObj) => void;
}

const NurseHeader: React.FC<NurseHeaderProps> = ({ month: propMonth, year: propYear, setMonth }) => {
  const user = useSelector((state: RootState) => state.auth.User);
  const [currentTime, setCurrentTime] = useState<string>(moment().format("hh:mm A"));
  useEffect(() => {
    const id = setInterval(() => setCurrentTime(moment().format("hh:mm A")), 1000);
    return () => clearInterval(id);
  }, []);

  // Fallback local state when used standalone (no props passed)
  const [localMonth, setLocalMonth] = useState<MonthObj>({
    name: moment().format("MMMM"),
    monthName: moment().format("MMMM"),
    label: moment().format("M"),
  });

  const activeMonth = propMonth ?? localMonth;
  const setActiveMonth = setMonth ?? setLocalMonth;

  const [leavesData, setLeavesData] = useState<any>();

  function getWeeksInMonth() {
    const firstDay = new Date(moment().year(), +activeMonth.label - 1, 1);
    const lastDay  = new Date(moment().year(), +activeMonth.label, 0);
    return Math.ceil(lastDay.getDate() / 7);
  }

  const numberOfWeeks = getWeeksInMonth();
  const weeks = generateWeeks(numberOfWeeks);
  const [week, setWeek] = useState(weeks[0]);

  const fetchStats = async () => {
    const res = await getShiftsRemainingLeavesWeeklyHours(
      +activeMonth.label,
      currentYear,
      week.label
    );
    if (res?.res) setLeavesData(res?.res);
  };

  useEffect(() => { fetchStats(); }, [week, activeMonth]);

  return (
    <div className="pb-5 pt-[56px] md:pt-[80px] px-4 md:px-7 bg-gradient-to-tll">
      {/* Mobile greeting */}
      <div className="mb-4 md:hidden">
        <Greeting role={user?.role as string} />
        <h2 className="text-white text-moon-24 font-semibold mb-1">{user?.employeeName}</h2>
        <div className="flex items-center gap-2 text-white/90 text-moon-12 flex-wrap">
          <span>{moment().format("DD MMMM YYYY")}</span>
          <span className="opacity-40">·</span>
          <span>{currentTime}</span>
          {user?.department && <><span className="opacity-40">·</span><span>{user.department}</span></>}
        </div>
      </div>

      <div className="flex items-stretch gap-2 md:gap-1 md:-mx-[4px]">
        {/* Greeting — desktop only */}
        <div className="hidden md:block md:w-1/5 md:px-[4px]">
          <Greeting role={user?.role as string} />
          <h2 className="text-white 2xl:text-moon-32 text-moon-24 font-semibold mb-2">{user?.employeeName}</h2>
          <div className="flex items-center gap-2 text-white/90 text-moon-12 flex-wrap">
            <span>{moment().format("DD MMMM YYYY")}</span>
            <span className="opacity-40">·</span>
            <span>{currentTime}</span>
            {user?.department && <><span className="opacity-40">·</span><span>{user.department}</span></>}
          </div>
        </div>

        {/* Stats tiles */}
        <div className="flex-1 md:w-3/4 md:px-[4px]">
          {/* Mobile: 4 stats in a row */}
          <div className="md:hidden flex flex-col gap-2">
            <div className="grid grid-cols-4 gap-2">
              <AccountStats
                title={"Schedules"}
                value={""}
                statsNumber={leavesData?.totalSchedules}
                outOff={""}
                options={[]}
                bgColor="bg-white"
                setOptions={setActiveMonth}
              />
              <AccountStats
                title={"Time-Off"}
                value={""}
                statsNumber={leavesData?.remainingLeaves}
                outOff={leavesData?.totalLeaves}
                options={[]}
                bgColor="bg-white"
                setOptions={setWeek}
              />
              <AccountStats
                title={"Hours"}
                value={""}
                statsNumber={leavesData?.hoursWorked}
                outOff={leavesData?.hoursAligned !== undefined ? Math.round(leavesData.hoursAligned) : ""}
                options={[]}
                bgColor="bg-white"
                setOptions={setWeek}
              />
              <AccountStats
                title={"On Calls"}
                value={""}
                statsNumber={leavesData?.onCallShifts ?? 0}
                outOff={""}
                options={[]}
                bgColor="bg-white"
                setOptions={setWeek}
              />
            </div>
          </div>

          {/* Desktop: 4-column grid */}
          <div className="hidden md:grid md:grid-cols-4 gap-1">
            <div>
              <AccountStats
                title={"Total Schedules"}
                value={""}
                statsNumber={leavesData?.totalSchedules}
                outOff={""}
                options={[]}
                bgColor="bg-white"
                setOptions={setActiveMonth}
              />
            </div>
            <div>
              <AccountStats
                title={"Remaining Time-Off"}
                value={""}
                statsNumber={leavesData?.remainingLeaves}
                outOff={leavesData?.totalLeaves}
                options={[]}
                bgColor="bg-white"
                setOptions={setWeek}
              />
            </div>
            <div>
              <AccountStats
                title={"Hours Worked"}
                value={""}
                statsNumber={leavesData?.hoursWorked}
                outOff={leavesData?.hoursAligned !== undefined ? Math.round(leavesData.hoursAligned) : ""}
                options={[]}
                bgColor="bg-white"
                setOptions={setWeek}
              />
            </div>
            <div>
              <AccountStats
                title={"On Calls"}
                value={""}
                statsNumber={leavesData?.onCallShifts ?? 0}
                outOff={""}
                options={[]}
                bgColor="bg-white"
                setOptions={setWeek}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NurseHeader;
